// ─── Wishlist ─────────────────────────────────────────────────────────────────
// Source of truth: the storefront API (MongoDB) via YJApiClient (api-client.js).
// Only the handle list is cached (via YJApiClient's stale-while-revalidate
// localStorage layer). Product data is always fetched fresh from
// /products/<handle>.js — never cached.
//
// Architecture
// ────────────
//   _api()          — lazy accessor for window.YJApiClient (keeps tests swappable)
//   _fetchProductItems() — fetches live product data for a list of handles
//   WishlistDrawer  — fetches from API + product endpoints on every open,
//                     then reconciles in place as wishlist:updated events arrive
// ──────────────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  // ── Localization ──────────────────────────────────────────────────────────
  // Strings are provided (per active Shopify locale) via window.YJ.strings.wishlist
  // in layout/theme.liquid. English defaults act as a fallback if unavailable.
  const _STRINGS = {
    empty: 'Your wishlist is empty',
    shop_all: 'Shop All',
    add_to_cart: 'Add to cart',
    added_to_cart: 'Added to cart ✓',
    adding: 'Adding…',
    try_again: 'Try again',
    notify: 'Notify me when back in stock',
    subscribing: 'Subscribing…',
    on_the_list_check: "You're on the list ✓",
    link_copied: 'Link copied!',
    something_wrong: 'Something went wrong',
    error_text: "We couldn't load your wishlist. Please try again.",
    sold_out: 'Sold out',
    login: 'Log in',
    register: 'register',
    guest_banner_html: '<a href="__LOGIN__">Log in</a> or <a href="__REGISTER__">register</a> to save your wishlist.',
    sign_in: 'Sign In',
    sign_in_to_view: 'Sign in to view your wishlist',
    create_account: 'Create an account',
  };

  function _t(key) {
    return window.YJ?.strings?.wishlist?.[key] ?? _STRINGS[key] ?? '';
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  function _isLoggedIn() {
    return !!(window.YJ?.customerId && window.YJApiClient);
  }

  /** Lazy accessor so tests can replace window.YJApiClient between calls. */
  function _api() { return window.YJApiClient; }

  // ── Handle cache ──────────────────────────────────────────────────────────
  // Only the handle list is persisted. Product data is intentionally not cached.
  // Caching + in-flight request dedup both live in YJApiClient (api-client.js) —
  // every wishlist.get() call here, however many concurrent callers there are,
  // is deduped and stale-while-revalidate'd at that layer.

  // ── Button state ──────────────────────────────────────────────────────────

  const BTN_STATES = {
    loading: { spinner: true,  disabled: true  },
    added:   { labelKey: 'added_to_cart', spinner: false, disabled: true  },
    error:   { labelKey: 'try_again',     spinner: false, disabled: true  },
    '':      { labelKey: 'add_to_cart',   spinner: false, disabled: false },
  };

  function _setBtnState(btn, state) {
    const { labelKey, spinner, disabled } = BTN_STATES[state] ?? BTN_STATES[''];
    const label = labelKey ? _t(labelKey) : '';
    btn.disabled      = disabled;
    btn.dataset.state = state;
    const labelEl   = btn.querySelector('.wishlist-item__add-label');
    const spinnerEl = btn.querySelector('.wishlist-item__add-spinner');
    if (labelEl)   labelEl.textContent = label;
    if (spinnerEl) spinnerEl.hidden    = !spinner;
  }

  /**
   * Temporarily replaces the text of a button's inner <span> with a feedback
   * label, then restores the original after `ms` milliseconds. Targets the
   * <span> so that sibling elements (e.g. SVG icons) are left untouched.
   */
  function _flashBtnLabel(btn, label, ms) {
    if (!btn) return;
    const span = btn.querySelector('span');
    const target = span ?? btn;
    const original = target.textContent.trim();
    target.textContent = label;
    setTimeout(() => { target.textContent = original; }, ms);
  }

  // ── WishlistStore ─────────────────────────────────────────────────────────

  window.WishlistStore = {

    /**
     * Returns the cached handle list as minimal stubs { handle, url }.
     * Product data (title, image, price, variants) is NOT included here —
     * the drawer fetches that fresh on every open.
     */
    async get() {
      const { handles } = await _api().wishlist.get();
      return handles.map(handle => ({ handle, url: `/products/${handle}` }));
    },

    async has(handle) {
      const { handles } = await _api().wishlist.get();
      return handles.includes(handle);
    },

    /**
     * Optimistically adds a handle to the local cache and syncs to the API.
     * The URL's ?variant= param (if present) is forwarded as the selected variant.
     * 
     */
    async add(product, { variantId: variantIdOverride } = {}) {
      const variantId = variantIdOverride
        ?? new URLSearchParams(window.location.search).get('variant')
        ?? product.variantId
        ?? null;

      try {
        await _api().wishlist.add(product.handle, variantId);
        return true;
      } catch (error) {
        console.error('Failed to add to wishlist:', product.handle, error);
        return false;
      }
    },

    /**
     * Optimistically removes a handle from the local cache and syncs to the API.
     */
    async remove(handle) {
      try {
        await _api().wishlist.remove(handle)
        return false;
      } catch (error) {
        console.error('Failed to remove from wishlist:', handle, error);
        return true;
      }
    },

   // WishlistStore
    async toggle(product) {
      if (await this.has(product.handle)) {
        return await this.remove(product.handle)
      }
      return await this.add(product)
    },

    /**
     * Updates the displayed price when the user changes the variant dropdown.
     * Each <option> carries its formatted price in data-price — no cache needed.
     */
    async setVariant(price, selectEl) {
      const item = selectEl?.closest('.wishlist-item');
      const priceEl = item?.querySelector('.wishlist-item__price');
      if (priceEl) priceEl.textContent = price;

      const handle    = item?.dataset.handle;
      const variantId = selectEl?.value;
      const option    = selectEl?.selectedOptions?.[0];
      const available = option ? option.dataset.available !== 'false' : true;

      // Swap the button between "Add to cart" and "Notify me when back in stock"
      // depending on the availability of the newly-selected variant.
      await _updateItemButton(item, available, variantId);

      if (handle && variantId) {
        this.add({ handle }, { variantId });
      }
    },

    /**
     * Primary action for a wishlist item. Delegates to addToCart for in-stock
     * variants, or handles the back-in-stock notify flow for sold-out ones:
     *   • Logged-in customer (email known) → subscribe directly via the API,
     *     no modal, and flip the button to "You're on the list".
     *   • Guest → open the BIS modal to collect an email, as before.
     */
    async itemPrimaryAction(handle, btn) {
      if (btn?.dataset.mode === 'notify') {
        const item      = btn.closest('.wishlist-item');
        const title     = item?.querySelector('.wishlist-item__title')?.textContent.trim() || '';
        const variantId = await _resolveVariantId(handle, btn.dataset.variantId);
        const email     = window.YJ?.customerEmail;

        if (email) {
          await _notifyDirect(btn, variantId, email);
        } else {
          _openBisModal(variantId, title);
        }
        return;
      }
      this.addToCart(handle, btn);
    },

    /**
     * Adds the currently-selected variant of a wishlist item to the Shopify cart.
     */
    async addToCart(handle, btn) {
      if (!btn || btn.disabled) return;
      _setBtnState(btn, 'loading');

      try {
        const variantId = await _resolveCartVariant(handle, btn);
        await _postToCart(variantId);
        _setBtnState(btn, 'added');
        setTimeout(() => _setBtnState(btn, ''), 2000);
      } catch {
        _setBtnState(btn, 'error');
        setTimeout(() => _setBtnState(btn, ''), 2500);
      }
    },

    /**
     * Fetches a share URL for the wishlist and either invokes the Web Share
     * API (mobile/supported browsers) or copies the link to the clipboard.
     * Provides brief "Link copied!" feedback on the triggering button.
     */
    async shareWishlist(btn) {
      if (btn) btn.disabled = true;
      try {
        const url = await _api().wishlist.fetchShareLink();
        if (navigator.share) {
          await navigator.share({ title: 'My Wishlist', url });
        } else {
          await navigator.clipboard.writeText(url);
          _flashBtnLabel(btn, _t('link_copied'), 2000);
        }
      } catch (err) {
        // AbortError means the user cancelled the native share sheet — not an error.
        if (err?.name !== 'AbortError') {
          console.error('Failed to share wishlist:', err);
          _flashBtnLabel(btn, _t('something_wrong'), 2500);
        }
      } finally {
        if (btn) btn.disabled = false;
      }
    },

    /**
     * Clears all items from the wishlist via the API and resets the drawer
     * to the empty state immediately without a re-fetch.
     */
    async clearWishlist(btn) {
      if (btn) btn.disabled = true;
      try {
        await _api().wishlist.clear();
        const drawer = document.querySelector('wishlist-drawer');
        if (drawer?._list) {
          drawer._list.innerHTML = _renderEmpty();
          drawer._ensureGuestBanner();
        }
      } catch (err) {
        console.error('Failed to clear wishlist:', err);
        _flashBtnLabel(btn, _t('something_wrong'), 2500);
      } finally {
        if (btn) btn.disabled = false;
      }
    },
  };

  // ── Cart helpers ──────────────────────────────────────────────────────────

  /**
   * Resolves a variant ID, falling back to a product's first available
   * variant (or its first variant if none are available) when `explicitId`
   * is missing. Centralizes the fallback strategy shared by every call site
   * that previously defaulted to an empty string.
   */
  async function _resolveVariantId(handle, explicitId) {
    if (explicitId) return explicitId;

    const product = await fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    const variant = product?.variants.find(v => v.available) ?? product?.variants?.[0];
    return variant ? String(variant.id) : '';
  }

  /**
   * Resolves the variant ID for addToCart.
   * Priority: live DOM select → first available variant.
   */
  async function _resolveCartVariant(handle, btn) {
    const selectEl = btn.closest('.wishlist-item')?.querySelector('.wishlist-item__variant-select');
    if (selectEl?.value) return selectEl.value;

    const variantId = await _resolveVariantId(handle);
    if (!variantId) throw new Error('no_variant');
    return variantId;
  }

  /**
   * Resolves the variant ID to notify for when a wishlist item's button
   * doesn't already carry one (e.g. rendered before a variant was ever
   * selected). Falls back to the product's first available variant, or its
   * first variant if none are available — mirroring _resolveCartVariant's
   * fallback strategy.
   */

  /** POSTs a variant to /cart/add.js and updates the cart drawer + count badge. */
  async function _postToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['cart-drawer'],
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'cart_error');
    if (data.sections?.['cart-drawer']) _updateCartDrawer(data.sections['cart-drawer']);
    _refreshCartCount();
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  function _updateCartDrawer(sectionHTML) {
    try {
      document.querySelector('cart-drawer')?.renderContents({
        sections: { 'cart-drawer': sectionHTML },
        id: null,
      });
    } catch { /* non-critical — count badge already updated */ }
  }

  function _refreshCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(({ item_count: count }) => {
        document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
          let span = bubble.querySelector('span[aria-hidden="true"]');
          if (count > 0) {
            if (!span) {
              span = document.createElement('span');
              span.setAttribute('aria-hidden', 'true');
              bubble.prepend(span);
            }
            span.textContent = String(count);
          } else {
            span?.remove();
          }
        });
      })
      .catch(() => {});
  }

  // ── Product data fetching ─────────────────────────────────────────────────
  // Always fetches fresh from /products/<handle>.js — no localStorage caching.
  // variantMap comes from the API response and determines the pre-selected variant.

  function _normalizeVariants(variants) {
    return variants.map(v => ({
      id:        String(v.id),
      title:     v.title,
      available: v.available,
      price:     _formatPrice(v.price),
    }));
  }

  async function _fetchProductItems(handles, variantMap = {}) {
    const results = await Promise.allSettled(
      handles.map(handle =>
        fetch(`/products/${encodeURIComponent(handle)}.js`)
          .then(r => r.ok ? r.json() : null)
          .then(product => {
            if (!product) return null;
            const storedVid = variantMap[handle] ? String(variantMap[handle]) : null;
            const variant   = storedVid
              ? (product.variants.find(v => String(v.id) === storedVid) ?? product.variants[0])
              : product.variants[0];
            return {
              handle,
              title:        product.title ?? handle,
              image:        product.featured_image ?? '',
              price:        _formatPrice(variant?.price ?? product.variants?.[0]?.price),
              url:          `/products/${handle}`,
              variantId:    variant ? String(variant.id) : '',
              variantTitle: variant?.title ?? '',
              variants:     _normalizeVariants(product.variants),
            };
          })
          .catch(() => null)
      )
    );
    return results.map(r => r.status === 'fulfilled' ? r.value : null).filter(Boolean);
  }

  // ── Rendering helpers ─────────────────────────────────────────────────────

  function _renderEmpty() {
    return `
      <div class="wishlist-drawer__empty">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <p class="wishlist-drawer__empty-text">${_esc(_t('empty'))}</p>
        <a href="/collections/shop-all" class="button button--primary"><span>${_esc(_t('shop_all'))}</span></a>
      </div>`;
  }

  function _renderGuestBanner() {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    const html = _t('guest_banner_html')
      .replace('__LOGIN__', `/account/login?return_url=${returnUrl}`)
      .replace('__REGISTER__', `/account/register?return_url=${returnUrl}`);
    return `
      <div class="wishlist-drawer__guest-banner">
        <p class="wishlist-drawer__guest-banner-text" style="margin-top: 0; font-size: 13px;">${html}</p>
      </div>`;
  }

  /**
   * Shown when the wishlist fails to load for a reason other than
   * "not logged in" (e.g. the API or a product fetch throws). This should
   * be rare — it means something is actually broken, not just an empty list.
   */
  function _renderError() {
    return `
      <div class="wishlist-drawer__error">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p class="wishlist-drawer__error-text">${_esc(_t('error_text'))}</p>
        <button type="button" class="button button--primary" onclick="document.querySelector('wishlist-drawer')?.open()">
          <span>${_esc(_t('try_again'))}</span>
        </button>
      </div>`;
  }

  function _renderItem(p) {
    return `
      <div class="wishlist-item" data-handle="${_esc(p.handle)}">
        <a href="${_esc(p.url)}" class="wishlist-item__image-link">
          <img
            class="wishlist-item__image"
            src="${_esc(p.image)}"
            alt="${_esc(p.title)}"
            width="80"
            height="100"
            loading="lazy"
          >
        </a>
        <div class="wishlist-item__details">
          <a href="${_esc(p.url)}" class="wishlist-item__title link link--text">
            ${_esc(p.title)}
          </a>
          ${_buildVariantSelect(p)}
          <div class="wishlist-item__price">${_esc(p.price)}</div>
          ${_buildAddButton(p)}
        </div>
        <button
          class="wishlist-item__remove"
          type="button"
          aria-label="Remove from wishlist"
          onclick="WishlistStore.remove('${_esc(p.handle)}')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.293 5.293a1 1 0 011.414 0L12 10.586l5.293-5.293a1 1 0 111.414 1.414L13.414 12l5.293 5.293a1 1 0 01-1.414 1.414L12 13.414l-5.293 5.293a1 1 0 01-1.414-1.414L10.586 12 5.293 6.707a1 1 0 010-1.414z" fill="currentColor"/>
          </svg>
        </button>
      </div>`;
  }

  /**
   * Builds the primary action button for a wishlist item.
   * If the pre-selected variant is out of stock the button becomes a
   * "Notify me when back in stock" trigger that opens the BIS modal;
   * otherwise it's a standard add-to-cart button. The mode is re-evaluated
   * whenever the shopper switches variants (see WishlistStore.setVariant).
   */
  function _buildAddButton(p) {
    const selected  = p.variants?.find(v => v.id === p.variantId);
    const available = selected ? selected.available : true;
    const mode      = available ? 'add' : 'notify';
    const label     = available ? _t('add_to_cart') : _t('notify');
    const ariaLabel = available
      ? `Add ${p.title} to cart`
      : `Notify me when ${p.title} is back in stock`;
    return `
          <button
            class="wishlist-item__add-btn"
            type="button"
            data-state=""
            data-mode="${mode}"
            data-variant-id="${_esc(p.variantId)}"
            aria-label="${_esc(ariaLabel)}"
            onclick="WishlistStore.itemPrimaryAction('${_esc(p.handle)}', this)"
          >
            <span class="wishlist-item__add-label">${_esc(label)}</span>
            <span class="wishlist-item__add-spinner" aria-hidden="true" hidden></span>
          </button>`;
  }

  function _buildVariantSelect({ variants, variantId, title, handle }) {
    if (!variants?.length) return '';
    if (variants.length === 1 && variants[0].title === 'Default Title') return '';

    const options = variants.map(v => `
          <option
            value="${_esc(v.id)}"
            data-price="${_esc(v.price)}"
            data-available="${v.available ? 'true' : 'false'}"
            ${v.id === variantId ? ' selected' : ''}
          >${_esc(v.title)}${!v.available ? ' – ' + _esc(_t('sold_out')) : ''}</option>`
    ).join('');

    return `
      <div class="wishlist-item__variant-wrapper">
        <select
          class="wishlist-item__variant-select"
          aria-label="Select size for ${_esc(title)}"
          onchange="WishlistStore.setVariant(this.selectedOptions[0].dataset.price, this)"
        >${options}</select>
      </div>`;
  }

  /**
   * Toggles a wishlist item's primary button between add-to-cart and
   * notify-me modes based on the availability of the currently-selected
   * variant. Called on every variant change.
   */
  async function _updateItemButton(item, available, variantId) {
    const btn = item?.querySelector('.wishlist-item__add-btn');
    if (!btn) return;
    const labelEl = btn.querySelector('.wishlist-item__add-label');
    const title   = item.querySelector('.wishlist-item__title')?.textContent.trim() || '';
    const handle  = item.dataset.handle;

    btn.dataset.variantId = await _resolveVariantId(handle, variantId);
    btn.dataset.state     = '';
    btn.disabled          = false;

    if (available) {
      btn.dataset.mode = 'add';
      if (labelEl) labelEl.textContent = _t('add_to_cart');
      btn.setAttribute('aria-label', `Add ${title} to cart`);
    } else {
      btn.dataset.mode = 'notify';
      if (labelEl) labelEl.textContent = _t('notify');
      btn.setAttribute('aria-label', `Notify me when ${title} is back in stock`);
    }
  }

  // ── Back-in-stock notify modal ─────────────────────────────────────────────
  // The modal itself lives in the shared wishlist-bis-modal.js module (exposed
  // as window.WishlistBIS) so it can be reused by the wishlist page too. This
  // thin wrapper just delegates to it.

  function _openBisModal(variantId, productTitle) {
    window.WishlistBIS?.open(variantId, productTitle);
  }

  /**
   * Subscribes a logged-in customer to a variant's back-in-stock alert
   * directly (no modal) using their known email. On success the button
   * becomes a disabled "You're on the list" confirmation; on failure it
   * briefly flashes an error and resets so the shopper can retry.
   */
  async function _notifyDirect(btn, variantId, email) {
    const labelEl  = btn.querySelector('.wishlist-item__add-label');
    const original = labelEl?.textContent ?? '';
    btn.disabled = true;
    if (labelEl) labelEl.textContent = _t('subscribing');

    try {
      await window.WishlistBIS?.subscribe(variantId, email);
      btn.dataset.mode = 'subscribed';
      if (labelEl) labelEl.textContent = _t('on_the_list_check');
    } catch (err) {
      console.error('Failed to subscribe to back-in-stock alert:', err);
      if (labelEl) labelEl.textContent = _t('try_again');
      btn.disabled = false;
      setTimeout(() => { if (labelEl) labelEl.textContent = original; }, 2500);
    }
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  function _formatPrice(decimalStr) {
    const num = parseFloat(decimalStr);
    if (isNaN(num)) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(navigator.language || 'en-US', { style: 'currency', currency }).format(num / 100);
    } catch {
      return `$${(num / 100).toFixed(2)}`;
    }
  }

  function _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── WishlistDrawer custom element ─────────────────────────────────────────

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this._overlay = this.querySelector('.wishlist-drawer__overlay');
      this._inner   = this.querySelector('.wishlist-drawer__inner');
      this._list    = this.querySelector('.wishlist-drawer__list');
      this._loading = false;
      this._syncToken = 0;
      this._overlay?.addEventListener('click', () => this.close());

      // When handles change while the drawer is open, reconcile the DOM in
      // place: drop items that disappeared, append ones that showed up
      // elsewhere (e.g. another browser/tab). No full re-fetch needed.
      document.addEventListener('wishlist:updated', ({ detail }) => {
        if (!this.classList.contains('active') || this._loading) return;
        this._syncDOM(detail?.handles ?? [], detail?.variantMap ?? {});
      });

      document.addEventListener('wishlist:open', () => {
        document.querySelector('wishlist-drawer')?.open();
      });
    }

    async open() {
      this._loading = true;
      this._syncToken++; // cancel any in-flight incremental sync — this render replaces it wholesale
      this._renderLoading();
      this.classList.add('active');
      document.body.style.overflow = 'hidden';
      if (this._inner) this._inner.removeAttribute('aria-hidden');
      this.querySelector('.wishlist-drawer__close')?.focus();

      try {
        // Always fetch from the API to get the latest handle list + variantMap.
        const data       = await _api().wishlist.get();
        const handles    = data?.handles    ?? [];
        const variantMap = data?.variantMap ?? {};
        const items      = handles.length ? await _fetchProductItems(handles, variantMap) : [];
        this._loading    = false;
        this._renderItems(items);
      } catch (err) {
        this._loading = false;
        if (err instanceof _api().AuthError) {
          this._list.innerHTML = _renderEmpty();
        } else {
          // Not an auth issue — something actually broke (API down, network
          // failure, etc). Show a distinct error state rather than silently
          // rendering the empty-wishlist message, which would misrepresent
          // a real failure as "you have no items."
          this._list.innerHTML = _renderError();
        }
      }

      // Whatever we just rendered above (items, empty state, or error state)
      // replaced the list's innerHTML wholesale, so the guest banner needs
      // to be re-applied on every path — not just the empty/AuthError one —
      // or it disappears the moment a guest has items in their wishlist.
      this._ensureGuestBanner();
    }

    /**
     * Shows the guest banner above the wishlist contents when the current
     * visitor isn't logged in. Idempotent: if the banner is already in the
     * DOM, this is a no-op, so callers can invoke it after every render
     * without worrying about duplicating or re-flashing it.
     */
    _ensureGuestBanner() {
      if (!this._list || _isLoggedIn()) return;
      if (this._list.querySelector('.wishlist-drawer__guest-banner')) return;
      this._list.insertAdjacentHTML('afterbegin', _renderGuestBanner());
    }

    /**
     * Reconciles the open drawer with a fresh handle list: removes items
     * whose handles disappeared, then fetches and appends items for handles
     * that showed up elsewhere (e.g. added from another browser/tab) —
     * existing nodes and scroll position are left untouched. Shows the empty
     * state when nothing is left, and clears it when the first item lands.
     * A sync token guards against overlapping calls (this one or a fresh
     * open()) racing an in-flight product fetch and appending twice.
     */
    async _syncDOM(handles, variantMap = {}) {
      if (!this._list) return;

      this._list.querySelectorAll('.wishlist-item').forEach(el => {
        if (!handles.includes(el.dataset.handle)) el.remove();
      });

      const existingHandles = new Set(
        Array.from(this._list.querySelectorAll('.wishlist-item')).map(el => el.dataset.handle)
      );
      const newHandles = handles.filter(handle => !existingHandles.has(handle));

      if (newHandles.length) {
        const token = ++this._syncToken;
        const items = await _fetchProductItems(newHandles, variantMap);
        if (token !== this._syncToken) return; // superseded by a newer sync or a full open()
        this._list.querySelector('.wishlist-drawer__empty')?.remove();
        items.forEach(item => this._list.insertAdjacentHTML('beforeend', _renderItem(item)));
      }

      if (!this._list.querySelector('.wishlist-item')) {
        this._list.innerHTML = _renderEmpty();
      }
      // Covers both cases: re-adding the banner if the empty-state branch
      // above just wiped it out, and leaving it alone if items remain and
      // it's still sitting there from the last render.
      this._ensureGuestBanner();
    }

    close() {
      this.classList.remove('active');
      document.body.style.overflow = '';
      if (this._inner) this._inner.setAttribute('aria-hidden', 'true');
    }

    _renderLoading() {
      if (!this._list) return;
      this._list.innerHTML = `
        <div class="wishlist-drawer__loading">
          <span class="wishlist-drawer__spinner"></span>
        </div>`;
    }

    _renderLoginPrompt() {
      if (!this._list) return;
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      this._list.innerHTML = `
        <div class="wishlist-drawer__login">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          <p class="wishlist-drawer__login-text">${_esc(_t('sign_in_to_view'))}</p>
          <a href="/account/login?return_url=${returnUrl}" class="button button--primary"><span>${_esc(_t('sign_in'))}</span></a>
          <a href="/account/register?return_url=${returnUrl}" class="wishlist-drawer__login-register">${_esc(_t('create_account'))}</a>
        </div>`;
    }

    _renderItems(items) {
      if (!this._list) return;
      this._list.innerHTML = items.length ? items.map(_renderItem).join('') : _renderEmpty();
    }
  }

  customElements.define('wishlist-drawer', WishlistDrawer);

  // ── WishlistButton custom element ─────────────────────────────────────────

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this._handle = this.dataset.handle;
      const btn = this.querySelector('button');
      if (!btn) return;

      btn.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        if (this._pending) return;
        this._pending = true;

        // Flip the heart right away so the tap paints immediately; the toggle
        // result below confirms it, or reverts it if the request failed.
        const wasActive = btn.getAttribute('aria-pressed') === 'true';
        this._setActive(!wasActive);

        let added = wasActive;
        try {
          const handle    = this.dataset.handle;
          const variantId = await _resolveVariantId(handle, this.dataset.variantId);
          added           = await WishlistStore.toggle({ handle, variantId });
        } catch (error) {
          console.error('Failed to toggle wishlist:', this.dataset.handle, error);
        } finally {
          this._pending = false;
        }

        this._setActive(added);

        if (added && this.dataset.openDrawer) {
          document.querySelector('wishlist-drawer')?.open();
        }
      });

      document.addEventListener('wishlist:updated', ({ detail }) => {
        this._wishlistUpdatedCallback(detail?.handles).catch(() => {});
      });

      // Defer initial state check until all scripts are ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this._wishlistUpdatedCallback().catch(() => {});
        });
      } else {
        this._wishlistUpdatedCallback().catch(() => {});
      }
    }

    /**
     * Reads membership straight off the event detail when available —
     * avoids every button on the page re-querying the wishlist on each
     * update. Falls back to a fetch for the initial DOMContentLoaded check,
     * which has no event detail to read from.
     */
    async _wishlistUpdatedCallback(handles) {
      this._setActive(handles ? handles.includes(this._handle) : await WishlistStore.has(this._handle));
    }

    _setActive(active) {
      const btn = this.querySelector('button');
      if (!btn) return;
      btn.setAttribute('aria-pressed', String(active));
      btn.classList.toggle('wishlist-btn--active', active);
    }
  }

  customElements.define('wishlist-button', WishlistButton);

  // ── Init ──────────────────────────────────────────────────────────────────

  function _init() {
    // If a guest saved items and just logged in, merge their guest wishlist
    // into their account. No-ops immediately (no network call) when there's
    // no pending guest wishlist to merge.
    if (_isLoggedIn()) {
      _api()?.wishlist.mergeGuestIfNeeded?.().catch(() => {});
    }

    document.querySelectorAll('[data-open-wishlist]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        document.querySelector('wishlist-drawer')?.open();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }

})();

if (typeof module !== 'undefined') {
  module.exports = { WishlistStore: window.WishlistStore };
}